import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Users, Baby, DollarSign, Home } from "lucide-react";

const LOGO = "/bailey-logo.png";

function BigStat({ icon, label, value, sub }) {
  return (
    <div
      className="bg-white border border-[#e6e2d6] rounded-2xl p-8 shadow-[0_20px_60px_-30px_rgba(26,41,71,0.20)]"
      data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center gap-2 text-stone-500 mb-4">
        {icon}
        <span className="label-eyebrow">{label}</span>
      </div>
      <div className="font-serif text-5xl md:text-6xl text-[#1a2947] tracking-tight leading-none">
        {value}
      </div>
      {sub && <div className="text-sm text-stone-500 mt-3">{sub}</div>}
    </div>
  );
}

export default function StatisticsPage() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get("/stats/public")
      .then((r) => alive && setStats(r.data))
      .catch(() => alive && setErr("Couldn't load stats — please try again."));
    return () => {
      alive = false;
    };
  }, []);

  const pct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      <header className="max-w-6xl w-full mx-auto px-6 md:px-10 pt-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="stats-home-link">
          <img
            src={LOGO}
            alt="Bailey Family Reunion"
            className="h-10 w-10 rounded-full object-cover ring-2 ring-[#1a2947]/10"
          />
          <span className="font-serif text-2xl text-stone-900">Bailey Family Reunion</span>
        </Link>
        <Link
          to="/"
          className="label-eyebrow text-stone-500 hover:text-[#1a2947] flex items-center gap-1"
        >
          <ArrowLeft className="h-4 w-4" /> Back home
        </Link>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-14">
        <div className="text-center mb-12">
          <div className="text-[10px] tracking-[0.16em] uppercase font-semibold text-white bg-[#1a2947] px-3 py-1 rounded-full inline-block mb-4">
            Denver · July 21-23, 2028
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-stone-900 tracking-tight">
            Our reunion so far
          </h1>
          <p className="text-stone-600 mt-3 max-w-xl mx-auto">
            A live snapshot of who's signed on, how many are already paid, and how much we've
            collected toward Denver.
          </p>
        </div>

        {err && (
          <div className="max-w-md mx-auto bg-red-50 border border-red-200 text-red-800 rounded-xl px-4 py-3 text-sm">
            {err}
          </div>
        )}

        {!stats && !err && (
          <div className="text-center text-stone-500">Loading…</div>
        )}

        {stats && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="stats-grid">
              <BigStat
                icon={<Users className="h-4 w-4" />}
                label="Adults paid"
                value={`${stats.adults_paid}`}
                sub={`out of ${stats.adults_total} adults · ${pct(stats.adults_paid, stats.adults_total)}%`}
              />
              <BigStat
                icon={<Baby className="h-4 w-4" />}
                label="Kids paid"
                value={`${stats.kids_paid}`}
                sub={`out of ${stats.kids_total} kids · ${pct(stats.kids_paid, stats.kids_total)}%`}
              />
              <BigStat
                icon={<DollarSign className="h-4 w-4" />}
                label="Collected"
                value={`$${Number(stats.collected || 0).toFixed(0)}`}
                sub="across all household members"
              />
            </div>

            <div className="mt-8 bg-white border border-[#e6e2d6] rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Home className="h-5 w-5 text-[#1a2947]" />
                <div>
                  <div className="label-eyebrow">Families signed on</div>
                  <div className="font-serif text-2xl text-stone-900">
                    {stats.families_paid}{" "}
                    <span className="text-stone-400">/ {stats.families}</span>
                  </div>
                </div>
              </div>
              <div className="w-full md:w-2/3">
                <div className="h-3 bg-[#eae5d9] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#1a2947] transition-all"
                    style={{ width: `${pct(stats.families_paid, stats.families)}%` }}
                    data-testid="stats-progress-bar"
                  />
                </div>
                <div className="text-xs text-stone-500 mt-2 text-right">
                  {pct(stats.families_paid, stats.families)}% of households have contributed
                </div>
              </div>
            </div>

            <p className="text-center text-xs text-stone-400 mt-10">
              Individual family details stay private — this page only shows totals.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
